import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  PinpointProvider, NavBar, PageHeader, AppFooter, Section, AccountMenu,
  Box, Flex, Grid, GridItem, Stack, InlineAlert, SessionCard, WavingHand,
} from "@pinpoint/design-system";
import Backend from "../utils/api.js";
import useDashboardData from "../hooks/useDashboardData.jsx";
import useVersion from "../hooks/useVersion.jsx";
import {
  StatRow, ActionLink, RecentVisits, ManagedSpaces, Certifications, DashboardSkeletons, fmtTime,
} from "./widgets.jsx";

export default function DashboardPage({ user }) {
  const history = useHistory();
  const { loading, error, data } = useDashboardData(user);
  const { version, environment } = useVersion();
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState(false);

  const initials = `${(user.firstName || "")[0] || ""}${(user.lastName || "")[0] || ""}`.toUpperCase() || "?";

  const handleSignOut = async () => {
    if (!data?.activeSession) return;
    setSigningOut(true);
    setSignOutError(false);
    const res = await Backend.endSession(data.activeSession.id);
    if (res.status === 200) {
      window.location.reload();
    } else {
      setSignOutError(true);
      setSigningOut(false);
    }
  };

  return (
    <PinpointProvider>
      <Flex direction="column" minH="100vh" bg="bg.canvas">
        <NavBar
          brand="Pinpoint"
          links={[
            { label: "Dashboard", value: "dashboard" },
            { label: "Settings", value: "settings" },
          ]}
          activeValue="dashboard"
          onSelect={(v) => { if (v === "settings") history.push("/profile"); else history.push("/dashboard"); }}
          onBrandClick={() => history.push("/dashboard")}
          maxW="6xl"
          actions={
            <AccountMenu
              initials={initials}
              name={`${user.firstName || ""} ${user.lastName || ""}`.trim()}
              email={user.email}
              items={[
                ...(data?.isAdmin ? [{ label: "Adminland", onSelect: () => history.push("/admin") }] : []),
                { label: "Sign out", onSelect: () => { window.location.href = "/logout"; } },
              ]}
            />
          }
        />
        <Box as="main" flex="1" w="full" maxW="6xl" mx="auto" px={{ base: 4, md: 6 }} py={6}>
          <PageHeader
            eyebrow={new Date().toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}
            title={<>Hey {user.firstName || "there"} <WavingHand /></>}
            description="A snapshot of your activity across makerspaces that use Pinpoint."
            mb={6}
          />

          {loading ? (
            <DashboardSkeletons />
          ) : error ? (
            <InlineAlert status="error" title="Couldn't load your dashboard" description="Please refresh the page. If this keeps happening, contact eds@umd.edu." />
          ) : (
            <Stack gap={6}>
              {signOutError && (
                <InlineAlert status="error" title="Couldn't sign you out" description="Please try again. If this keeps happening, contact eds@umd.edu." />
              )}

              {data.activeSession && (
                <SessionCard
                  active
                  spaceName={data.spaceNames[data.activeSession.space_id] || data.activeSession.space_id}
                  since={fmtTime(data.activeSession.startedAt)}
                  autoEndAt={fmtTime(data.activeSession.autoEndAt)}
                  onCheckOut={handleSignOut}
                  checkingOut={signingOut}
                />
              )}

              <StatRow stats={data.stats} certCount={data.certifications.count} />

              <Grid templateColumns={{ base: "1fr", lg: "1.6fr 1fr" }} gap={4}>
                <GridItem minW={0}>
                  <Section label="Recent visits" action={<ActionLink label="View all activity →" />}>
                    <RecentVisits visits={data.recentVisits} spaceNames={data.spaceNames} />
                  </Section>
                </GridItem>
                <GridItem>
                  <Stack gap={4}>
                    {data.isAdmin && data.managedSpaces.length > 0 && (
                      <Section label="Spaces you manage" action={<ActionLink label="Open Adminland →" onActivate={() => history.push("/admin")} />}>
                        <ManagedSpaces spaces={data.managedSpaces} spaceNames={data.spaceNames} />
                      </Section>
                    )}
                    <Section label="Your tools">
                      <Certifications
                        tools={data.certifications.tools}
                        connected={data.badgeLinked}
                        onConnect={() => history.push("/profile")}
                      />
                    </Section>
                  </Stack>
                </GridItem>
              </Grid>
            </Stack>
          )}
        </Box>
        <AppFooter version={version} environment={environment} />
      </Flex>
    </PinpointProvider>
  );
}
